import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import {
  HiOutlineXMark,
  HiOutlineUserPlus,
  HiOutlineClipboardDocument
} from 'react-icons/hi2';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import './UserInvitation.scss';
import {
  createUserInvitation
} from '../../../../../../../../../../crud/users_api/user_invitations';
import {
  getPuestosByLocalId
} from '../../../../../../../../../../crud/management_api/puestos';
import {
  getAllRoles
} from '../../../../../../../../../../crud/configuration_api/roles';

export interface PuestoOption {
  id: number;
  name: string;
  description: string;
}

export interface RoleOption {
  id: number;
  name: string;
  description: string;
}

const fetchPuestos = async (): Promise<PuestoOption[]> => {
  const userContext = localStorage.getItem('user');
  const localId = userContext ? JSON.parse(userContext).local_id : 0;
  const resp = await getPuestosByLocalId(localId);
  return resp?.data?.map((p: any) => ({
    id: p.puesto_id,
    name: p.puesto_name,
    description: p.puesto_description
  })) ?? [];
};

const fetchRoles = async (): Promise<RoleOption[]> => {
  const resp = await getAllRoles();
  return resp?.data?.map((r: any) => ({
    id: r.rol_id,
    name: r.rol_name,
    description: r.rol_description || 'Sin descripción'
  })) ?? [];
};

interface UserInvitationProps {
  show: boolean;
  handleClose: () => void;
  onCreate: (inv: any) => void;
}

const UserInvitation: React.FC<UserInvitationProps> = ({
  show,
  handleClose,
  onCreate
}) => {
  const [name, setName] = useState('');
  const [puestos, setPuestos] = useState<PuestoOption[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [selectedPuesto, setSelectedPuesto] = useState<PuestoOption | null>(null);
  const [selectedRol, setSelectedRol] = useState<RoleOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [invitationCode, setInvitationCode] = useState<string | null>(null);
  const baseUrl = window.location.origin;

  useEffect(() => {
    fetchPuestos().then(setPuestos);
    fetchRoles().then(setRoles);
  }, []);

  const handleSubmit = async () => {
    if (!name || !selectedPuesto || !selectedRol) {
      toast.error('Por favor, completa el nombre, puesto y rol antes de continuar.');
      return;
    }
    setIsLoading(true);
    try {
      const resp = await createUserInvitation({
        nombre: name,
        puesto_id: selectedPuesto.id,
        rol_id: selectedRol.id
      });
      const code = resp.data?.invitation_code;
      if (code) {
        setInvitationCode(code);
        setShowShareModal(true);
        onCreate({
          invitation_id: resp.data.invitation_id,
          invitation_code: code,
          nombre: name,
          puesto_name: selectedPuesto.name,
          rol_name: selectedRol.name,
          valid: true,
          timestamp: new Date().toISOString()
        });
        toast.success('Invitación creada con éxito.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Hubo un problema al crear la invitación.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!invitationCode) return;
    const link = `${baseUrl}/UserInvitationPage/${invitationCode}`;
    navigator.clipboard.writeText(link)
      .then(() => toast.success('Enlace copiado'))
      .catch(() => toast.error('Error al copiar el enlace'))
      .finally(() => {
        setShowShareModal(false);
        handleClose();
      });
  };

  const handleShareWhatsApp = () => {
    if (!invitationCode) return;
    const link = `${baseUrl}/UserInvitationPage/${invitationCode}`;
    const msg = `¡Hola! Te invito a unirte usando este enlace: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    setShowShareModal(false);
    handleClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered dialogClassName="mp-modal">
        <div className="mp-modal-header">
          <div className="form-modal-title-container">
            <h5 className="form-modal-title">Crear Invitación</h5>
          </div>
          <button className="mp-modal-close" onClick={handleClose} disabled={isLoading}>
            <HiOutlineXMark size={18} />
          </button>
        </div>

        <div className="mp-modal-body">
          <div className="mp-form-group">
            <label className="mp-form-label">Nombre del usuario *</label>
            <input className="mp-form-input" type="text" placeholder="Ingrese el nombre" value={name} onChange={e => setName(e.target.value)} disabled={isLoading} />
          </div>

          <div className="mp-form-group">
            <label className="mp-form-label">Puesto *</label>
            <div className="mp-form-chips">
              {puestos.map(puesto => (
                <button key={puesto.id} type="button" className={`mp-form-chip ${selectedPuesto?.id === puesto.id ? 'selected' : ''}`} onClick={() => setSelectedPuesto(puesto)} disabled={isLoading}>
                  {puesto.name}
                </button>
              ))}
            </div>
          </div>

          <div className="mp-form-group">
            <label className="mp-form-label">Rol *</label>
            <div className="mp-form-chips">
              {roles.map(rol => (
                <button key={rol.id} type="button" className={`mp-form-chip ${selectedRol?.id === rol.id ? 'selected' : ''}`} onClick={() => setSelectedRol(rol)} disabled={isLoading}>
                  {rol.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mp-modal-footer">
          <button className="mp-modal-btn-cancel" onClick={handleClose} disabled={isLoading}>Cancelar</button>
          <button className="mp-modal-btn-submit" onClick={handleSubmit} disabled={isLoading || !name || !selectedPuesto || !selectedRol}>
            {isLoading ? 'Creando...' : (<><HiOutlineUserPlus size={16} /> Crear Invitación</>)}
          </button>
        </div>
      </Modal>

      <Modal show={showShareModal} onHide={() => setShowShareModal(false)} centered dialogClassName="mp-modal">
        <div className="mp-modal-header">
          <div className="form-modal-title-container">
            <h5 className="form-modal-title">Compartir Invitación</h5>
          </div>
          <button className="mp-modal-close" onClick={() => { setShowShareModal(false); handleClose(); }}>
            <HiOutlineXMark size={18} />
          </button>
        </div>

        <div className="mp-modal-body">
          <p style={{ textAlign: 'center', color: 'var(--text-color-muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>
            Comparte este enlace para invitar a la persona:
          </p>
          {invitationCode && (
            <div className="invitation-link-card">
              <code>{baseUrl}/UserInvitationPage/{invitationCode}</code>
            </div>
          )}
        </div>

        <div className="mp-modal-footer">
          <button className="mp-modal-btn-cancel" onClick={handleCopyLink}>
            <HiOutlineClipboardDocument size={16} /> Copiar enlace
          </button>
          <button className="mp-modal-btn-submit" onClick={handleShareWhatsApp}>
            <FaWhatsapp size={16} /> WhatsApp
          </button>
        </div>
      </Modal>
    </>
  );
};

export default UserInvitation;
